/** Configurações › Honorários: tabela própria do escritório + seccionais da OAB (importação, unidade, ativas) + modelos antigos. */
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime, fmtMoney, parseMoneyInput } from "@/lib/format";
import { confirmar, toast } from "@/ui/feedback";
import { Badge, Card, DataTable, ErrorBox, Field, FormGrid, Loading, Modal, Tabs, useAct } from "@/ui/kit";
import { OfficeItemModal, pct, RefLines, type OabTable, type OfficeItem } from "@/ui/fees";
import { FeeSettings } from "./SettingsPlatform";

type T = "escritorio" | "oab" | "modelos";

export function FeesSettings(): JSX.Element {
  const [tab, setTab] = useState<T>("escritorio");
  return (
    <div className="stack gap-12">
      <Tabs<T> value={tab} onChange={setTab} tabs={[
        { key: "escritorio", label: "Tabela do escritório" }, { key: "oab", label: "Tabelas da OAB" }, { key: "modelos", label: "Modelos antigos (por tipo de ação)" },
      ]} />
      {tab === "escritorio" && <OfficeItems />}
      {tab === "oab" && <OabTables />}
      {tab === "modelos" && <><div className="muted small">Modelos anteriores à tabela do escritório. Continuam valendo para tipos de ação sem item na tabela do escritório; os ativos já foram copiados para lá.</div><FeeSettings /></>}
    </div>
  );
}

// ================================================================ escritório

function OfficeItems(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["honorarios-escritorio"], queryFn: () => api.get<OfficeItem[]>("/honorarios/escritorio") });
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<Partial<OfficeItem> | null>(null);
  const [base, setBase] = useState(false);
  const rows = (data ?? []).filter((i) => !q || `${i.code ?? ""} ${i.title} ${i.section ?? ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card title="Tabela de honorários do escritório" actions={<div className="row">
      <input className="input" placeholder="Filtrar…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} />
      <button className="btn small" onClick={() => setBase(true)}>Montar a partir da OAB</button>
      <button className="btn small primary" onClick={() => setEdit({})}>Novo item</button>
    </div>}>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Itens livres (consulta, notificação, cada tipo de ação…). Cada item pode ser ligado a tipos de ação — vira a sugestão no caso e no contrato — e ao item
        equivalente de cada seccional da OAB, para comparar. A equipe consulta em <b>Honorários</b> no menu. Edição em massa: Planilha mestre › Tabela de honorários do escritório.
      </div>
      <ErrorBox error={error} />
      {!data ? <Loading /> : (
        <DataTable rows={rows} rowKey={(i) => i.id} onRowClick={setEdit} rowClass={(i) => (i.active ? "" : "row-muted")} empty="Nenhum item. Use “Novo item” ou “Montar a partir da OAB”."
          columns={[
            { key: "c", label: "Código", width: 80, render: (i) => i.code ?? "" },
            { key: "t", label: "Serviço", render: (i) => <>{i.title}{i.section && <div className="muted tiny">{i.section}</div>}</> },
            { key: "v", label: "Sugerido", width: 120, align: "right", render: (i) => (i.amount ? fmtMoney(i.amount) : "") },
            { key: "m", label: "Mínimo", width: 120, align: "right", render: (i) => (i.minAmount ? fmtMoney(i.minAmount) : "") },
            { key: "p", label: "%", width: 120, render: (i) => <span className="small">{i.percentNote ?? pct(i.percent)}</span> },
            { key: "a", label: "Tipos de ação", width: 90, render: (i) => (i.subjectTypeIds.length ? <Badge>{i.subjectTypeIds.length}</Badge> : <span className="muted small">—</span>) },
            { key: "o", label: "OAB", render: (i) => <RefLines refs={i.refs} mine={i.amount ? Number(i.amount) : i.minAmount ? Number(i.minAmount) : null} /> },
          ]} />
      )}
      {edit && <OfficeItemModal item={edit} onClose={() => setEdit(null)} />}
      {base && <FromOabModal onClose={() => setBase(false)} />}
    </Card>
  );
}

function FromOabModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { data } = useQuery({ queryKey: ["honorarios-oab"], queryFn: () => api.get<OabTable[]>("/honorarios/oab") });
  const loaded = (data ?? []).filter((t) => t.loaded);
  const [sec, setSec] = useState("");
  const [adj, setAdj] = useState("0");
  const go = useAct(() => api.post<{ created: number; skipped: number }>("/config/honorarios/escritorio/da-oab", { seccional: sec || loaded[0]?.seccional, adjustPct: Number(adj.replace(",", ".")) || 0 }),
    [["honorarios-escritorio"], ["honorarios-consulta"]], (r) => { toast.ok(`${r.created} item(ns) criados${r.skipped ? ` · ${r.skipped} já existiam ou sem valor` : ""}`); onClose(); }, false);
  return (
    <Modal title="Montar a tabela do escritório a partir da OAB" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={!loaded.length || go.isPending} onClick={() => go.mutate()}>Criar itens</button></>}>
      {!loaded.length ? <div className="muted">Importe uma tabela da OAB primeiro (aba “Tabelas da OAB”).</div> : (
        <FormGrid cols={2}>
          <Field label="Seccional base"><select className="input" value={sec || loaded[0]!.seccional} onChange={(e) => setSec(e.target.value)}>{loaded.map((t) => <option key={t.seccional} value={t.seccional}>OAB-{t.seccional} ({t.items.length} itens)</option>)}</select></Field>
          <Field label="Ajuste sobre o valor da OAB (%)" hint="ex.: 10 = 10% acima; -5 = 5% abaixo"><input className="input" value={adj} onChange={(e) => setAdj(e.target.value)} /></Field>
        </FormGrid>
      )}
      <div className="muted small pad">Cada item da seccional vira um item do escritório (valor convertido para reais pelo valor vigente da unidade, com o ajuste), já ligado ao item da OAB para comparar. Itens já ligados não são duplicados. Depois, ajuste os valores e ligue os tipos de ação.</div>
      <ErrorBox error={go.error} />
    </Modal>
  );
}

// ================================================================ OAB

interface Preview { token: string; unitName: string; unitValue: string | null; unitRef: string | null; warnings: string[]; total: number; items: Array<{ code: string; section: string | null; description: string; amount: string | null; units: string | null; percent: string | null; percentNote: string | null }> }

function OabTables(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["honorarios-oab"], queryFn: () => api.get<OabTable[]>("/honorarios/oab") });
  const [view, setView] = useState<string | null>(null);
  const [prev, setPrev] = useState<{ sec: string; p: Preview } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [adding, setAdding] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const [fileSec, setFileSec] = useState<string | null>(null);
  const fromSite = async (t: OabTable) => {
    const url = window.prompt(`Endereço do PDF oficial da OAB-${t.seccional}:`, t.sourceUrl ?? "https://");
    if (!url) return;
    setBusy(t.seccional); setErr(null);
    try { setPrev({ sec: t.seccional, p: await api.post<Preview>(`/config/honorarios/oab/${t.seccional}/previa`, { url }) }); } catch (e) { setErr(e); } finally { setBusy(null); }
  };
  const fromFile = async (f: File, sec: string) => {
    setBusy(sec); setErr(null);
    try { const fd = new FormData(); fd.append("file", f); setPrev({ sec, p: await api.upload<Preview>(`/config/honorarios/oab/${sec}/previa`, fd) }); } catch (e) { setErr(e); } finally { setBusy(null); }
  };
  if (!data) return <Card title="Tabelas da OAB"><ErrorBox error={error} /><Loading /></Card>;
  const shown = data.find((t) => t.seccional === view);
  return (
    <div className="stack gap-12">
      <div className="row"><span className="muted small grow">Seccionais “ativas” entram nas comparações e sugestões. Para atuar em outro estado, adicione a seccional e importe a tabela (PDF oficial ou planilha modelo).</span>
        <button className="btn small primary" onClick={() => setAdding(true)}>+ Adicionar seccional</button></div>
      <div className="grid cols-3">
        {data.map((t) => <OabCard key={t.seccional} t={t} busy={busy === t.seccional} onSite={() => void fromSite(t)} onFile={() => { setFileSec(t.seccional); setTimeout(() => file.current?.click(), 0); }} onView={() => setView(view === t.seccional ? null : t.seccional)} open={view === t.seccional} />)}
      </div>
      <input ref={file} type="file" accept="application/pdf,.pdf,.xlsx" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f && fileSec) void fromFile(f, fileSec); }} />
      <ErrorBox error={err} />
      {shown && <OabItems t={shown} />}
      {prev && <PreviewModal sec={prev.sec} p={prev.p} onClose={() => setPrev(null)} />}
      {adding && <AddSecModal onClose={() => setAdding(false)} />}
    </div>
  );
}

function AddSecModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ seccional: "", unitName: "R$", unitValue: "", sourceUrl: "" });
  const m = useAct(() => api.post("/config/honorarios/seccionais", { seccional: f.seccional, unitName: f.unitName, unitValue: parseMoneyInput(f.unitValue) || null, sourceUrl: f.sourceUrl || null }), [["honorarios-oab"]], onClose, "Seccional adicionada");
  return (
    <Modal title="Adicionar seccional da OAB" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || !/^[A-Za-z]{2}$/.test(f.seccional)} onClick={() => m.mutate()}>Adicionar</button></>}>
      <FormGrid cols={2}>
        <Field label="UF" required><input className="input" maxLength={2} value={f.seccional} onChange={(e) => setF({ ...f, seccional: e.target.value.toUpperCase() })} placeholder="ex.: SP" autoFocus /></Field>
        <Field label="Unidade da tabela" hint="R$ = valores em reais; ou a unidade da seccional (URH, UAD, UHA…)"><input className="input" maxLength={8} value={f.unitName} onChange={(e) => setF({ ...f, unitName: e.target.value.toUpperCase() })} /></Field>
        <Field label="Valor da unidade (R$)"><input className="input" value={f.unitValue} onChange={(e) => setF({ ...f, unitValue: e.target.value })} disabled={f.unitName === "R$"} /></Field>
        <Field label="PDF oficial (endereço, opcional)" span={2}><input className="input" value={f.sourceUrl} onChange={(e) => setF({ ...f, sourceUrl: e.target.value })} placeholder="https://www.oab….org.br/…pdf" /></Field>
      </FormGrid>
      <div className="muted small pad">Depois de adicionar: “Importar do site oficial” (PDF) ou baixe a planilha modelo, preencha e envie.</div>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

function OabCard({ t, busy, onSite, onFile, onView, open }: { t: OabTable; busy: boolean; onSite: () => void; onFile: () => void; onView: () => void; open: boolean }): JSX.Element {
  const [unit, setUnit] = useState(t.unitValue ? Number(t.unitValue).toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "");
  const [ref, setRef] = useState(t.unitRef ?? "");
  const save = useAct((b: Record<string, unknown>) => api.patch(`/config/honorarios/oab/${t.seccional}`, b), [["honorarios-oab"], ["honorarios-consulta"], ["honorarios-escritorio"]], undefined, "Seccional atualizada");
  return (
    <Card title={`OAB-${t.seccional}`} actions={t.loaded ? <Badge tone={t.active ? "ok" : "muted"}>{t.items.length} itens{t.active ? "" : " · inativa"}</Badge> : <Badge tone="muted">sem itens</Badge>}>
      <div className="stack gap-6 small">
        <div className="muted">{t.title}</div>
        {t.importedAt && <div className="muted tiny">Importada em {fmtDateTime(t.importedAt)}</div>}
        {t.unitName !== "R$" && t.exists && (
          <div className="row nowrap">
            <span>{t.unitName}: R$</span>
            <input className="input" style={{ width: 90 }} value={unit} onChange={(e) => setUnit(e.target.value)} />
            <input className="input" style={{ width: 100 }} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="mês/ano" />
            <button className="btn small" disabled={save.isPending} onClick={() => save.mutate({ unitValue: parseMoneyInput(unit) || null, unitRef: ref || null })}>Salvar</button>
          </div>
        )}
        {t.exists && <label className="check"><input type="checkbox" checked={t.active} onChange={(e) => save.mutate({ active: e.target.checked })} /> Usar nas comparações e sugestões</label>}
        <div className="row wrap">
          <button className="btn small primary" disabled={busy} onClick={onSite}>{busy ? "Lendo…" : "Importar do site oficial"}</button>
          <button className="btn small" disabled={busy} onClick={onFile}>Enviar PDF ou planilha</button>
          {t.exists && <button className="btn small ghost" onClick={() => void api.download(`/config/honorarios/oab/${t.seccional}/planilha`, `OAB-${t.seccional}-honorarios.xlsx`)}>Planilha modelo</button>}
          {t.loaded && <button className="btn small ghost" onClick={onView}>{open ? "Fechar" : "Consultar"}</button>}
          {t.sourceUrl && <a className="link-btn small" href={t.sourceUrl} target="_blank" rel="noreferrer">PDF oficial ↗</a>}
        </div>
        <ErrorBox error={save.error} />
      </div>
    </Card>
  );
}

function OabItems({ t }: { t: OabTable }): JSX.Element {
  const [q, setQ] = useState("");
  const rows = t.items.filter((i) => !q || `${i.code} ${i.description} ${i.section ?? ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card title={`Tabela OAB-${t.seccional}${t.unitRef ? ` · ${t.unitName} ${t.unitRef}` : ""}`} actions={<input className="input" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240 }} />}>
      <DataTable rows={rows} rowKey={(i) => i.id} columns={[
        { key: "c", label: "Item", width: 80, render: (i) => i.code },
        { key: "d", label: "Descrição", render: (i) => <>{i.description}{i.section && <div className="muted tiny">{i.section}</div>}</> },
        { key: "u", label: t.unitName === "R$" ? "" : t.unitName, width: 70, align: "right", render: (i) => (i.units ? Number(i.units).toLocaleString("pt-BR") : "") },
        { key: "v", label: "Valor (R$)", width: 120, align: "right", render: (i) => (i.value != null ? fmtMoney(i.value) : "—") },
        { key: "p", label: "Percentual", width: 160, render: (i) => <span className="small">{i.percentNote ?? pct(i.percent)}</span> },
      ]} />
    </Card>
  );
}

function PreviewModal({ sec, p, onClose }: { sec: string; p: Preview; onClose: () => void }): JSX.Element {
  const [unit, setUnit] = useState(p.unitValue ? Number(p.unitValue).toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "");
  const [ref, setRef] = useState(p.unitRef ?? "");
  const apply = useAct(() => api.post<{ items: number }>(`/config/honorarios/oab/${sec}/aplicar`, { token: p.token, unitValue: parseMoneyInput(unit) || null, unitRef: ref || null }),
    [["honorarios-oab"], ["honorarios-escritorio"], ["honorarios-consulta"]], (r) => { toast.ok(`OAB-${sec}: ${r.items} itens gravados`); onClose(); }, false);
  return (
    <Modal title={`Prévia — OAB-${sec}: ${p.total} itens lidos`} onClose={onClose} wide="xl"
      footer={<><button className="btn" onClick={onClose}>Descartar</button><button className="btn primary" disabled={apply.isPending || !p.total} onClick={async () => { if (await confirmar(`Substituir a tabela da OAB-${sec} por estes ${p.total} itens? Os vínculos são mantidos pelo código do item.`, { confirm: "Aplicar" })) apply.mutate(); }}>Aplicar</button></>}>
      <div className="stack gap-8">
        {p.warnings.map((w) => <div key={w} className="warn-box small">{w}</div>)}
        {p.unitName !== "R$" && (
          <div className="row nowrap small">
            <span>Valor da {p.unitName}: R$</span>
            <input className="input" style={{ width: 100 }} value={unit} onChange={(e) => setUnit(e.target.value)} />
            <input className="input" style={{ width: 120 }} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="mês/ano" />
            <span className="muted">confira com o valor vigente divulgado pela seccional</span>
          </div>
        )}
        <div className="muted small">Confira os itens: a leitura do PDF é automática e pode errar em textos em várias colunas. Se não ficar bom, use a planilha modelo.</div>
        <div style={{ maxHeight: 460, overflow: "auto" }}>
          <table className="table small">
            <thead><tr><th>Item</th><th>Descrição</th><th style={{ textAlign: "right" }}>R$</th><th style={{ textAlign: "right" }}>{p.unitName === "R$" ? "" : p.unitName}</th><th>%</th></tr></thead>
            <tbody>{p.items.map((i) => (
              <tr key={i.code} className={!i.amount && !i.units && !i.percent ? "row-muted" : ""}>
                <td>{i.code}</td><td>{i.description}</td><td style={{ textAlign: "right" }}>{i.amount ? fmtMoney(i.amount) : ""}</td>
                <td style={{ textAlign: "right" }}>{i.units ? Number(i.units).toLocaleString("pt-BR") : ""}</td><td>{i.percentNote ?? pct(i.percent)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <ErrorBox error={apply.error} />
      </div>
    </Modal>
  );
}
